import moment from "moment";

export interface SemifinishedRequirementFlatRow {
  product_id: string | number;
  product_name: string;
  semifinished_id: string | number;
  semifinished_name: string;
  month: string;
  consumption_kg: any;
  requirement_kg: any;
}

export interface SemifinishedRequirementItem {
  key: string;
  productId: string | number;
  productName: string;
  semifinishedId: string | number | null;
  semifinishedName: string | null;
  months: {
    [month: string]: { consumptionKg: any; requirementKg: any };
  };
}

/** Полный список месяцев периода в виде 'YYYY-MM' (включительно). */
export function buildMonthsList(
  dateFrom: any,
  dateTo: any
): string[] {
  if (!dateFrom || !dateTo) {
    return [];
  }

  const start = moment(dateFrom).startOf("month");
  const end = moment(dateTo).startOf("month");

  if (end.isBefore(start)) {
    return [];
  }

  const months: string[] = [];
  for (
    let m = start.clone();
    m.isSameOrBefore(end);
    m.add(1, "month")
  ) {
    months.push(m.format("YYYY-MM"));
  }

  return months;
}

/** Заголовок месяца для шапки: 'Сентябрь 2026'. */
export function monthLabel(month: string): string {
  const label = moment(`${month}-01`).format("MMMM YYYY");
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** id продукта (число или d:products:N) → doc-id строки для batch param_ids. */
export function toProductDocId(id: any): string {
  const raw = String(id).trim();
  if (/^d:products:\d+$/.test(raw)) {
    return raw;
  }
  if (/^\d+$/.test(raw)) {
    return `d:products:${raw}`;
  }
  return raw;
}

/** Фильтры экрана → параметры batch reports.semifinished_requirement. */
export function buildQueryParams(filter: any) {
  const products = (filter.products || []) as any[];
  const semifinisheds = (filter.semifinisheds || []) as any[];

  const params: any = {
    date_from: filter.date_from
      ? moment(filter.date_from)
          .startOf("month")
          .format("YYYY-MM-DD")
      : null,
    date_to: filter.date_to
      ? moment(filter.date_to)
          .endOf("month")
          .format("YYYY-MM-DD")
      : null,
    product_ids:
      products.length > 0 ? products.map(id => toProductDocId(id)) : null,
    semifinished_ids:
      semifinisheds.length > 0
        ? semifinisheds.map(id => toProductDocId(id))
        : null
  };

  return params;
}

function toNumber(value: any): number {
  const num = Number(value);
  return Number.isNaN(num) ? 0 : num;
}

/**
 * Суммы колонок месяца для шапки таблицы: потребление — по уникальным
 * продуктам (в режиме разбивки оно повторяется на строках полуфабрикатов),
 * потребность — по всем строкам. Округление до 3 знаков.
 */
export function columnTotals(
  items: SemifinishedRequirementItem[],
  months: string[]
): { [month: string]: { consumptionKg: number; requirementKg: number } } {
  const totals: {
    [month: string]: { consumptionKg: number; requirementKg: number };
  } = {};
  const countedProducts: { [month: string]: Set<string | number> } = {};

  (months || []).forEach(month => {
    totals[month] = { consumptionKg: 0, requirementKg: 0 };
    countedProducts[month] = new Set();
  });

  (items || []).forEach(item => {
    (months || []).forEach(month => {
      const cell = item.months[month];
      if (!cell) {
        return;
      }
      if (!countedProducts[month].has(item.productId)) {
        countedProducts[month].add(item.productId);
        totals[month].consumptionKg += toNumber(cell.consumptionKg);
      }
      totals[month].requirementKg += toNumber(cell.requirementKg);
    });
  });

  (months || []).forEach(month => {
    totals[month].consumptionKg =
      Math.round(totals[month].consumptionKg * 1000) / 1000;
    totals[month].requirementKg =
      Math.round(totals[month].requirementKg * 1000) / 1000;
  });

  return totals;
}

/**
 * Плоские строки batch → строки «продукт × полуфабрикат» (splitBySemifinished)
 * или «продукт» с суммой потребности по всем полуфабрикатам состава.
 * Потребление продукта в строках повторяется — при агрегации не суммируется.
 */
export function pivotSemifinishedRequirement(
  rows: SemifinishedRequirementFlatRow[],
  months: string[],
  splitBySemifinished: boolean = true
): SemifinishedRequirementItem[] {
  const map = new Map<string, SemifinishedRequirementItem>();

  (rows || []).forEach((row: SemifinishedRequirementFlatRow) => {
    const key = splitBySemifinished
      ? `${row.product_id}-${row.semifinished_id}`
      : `${row.product_id}`;

    if (!map.has(key)) {
      map.set(key, {
        key,
        productId: row.product_id,
        productName: row.product_name,
        semifinishedId: splitBySemifinished ? row.semifinished_id : null,
        semifinishedName: splitBySemifinished ? row.semifinished_name : null,
        months: {}
      });
    }

    const item = map.get(key) as SemifinishedRequirementItem;
    const cell = item.months[row.month];

    if (!cell || splitBySemifinished) {
      item.months[row.month] = {
        consumptionKg: row.consumption_kg,
        requirementKg: row.requirement_kg
      };
    } else {
      // режим «по продукту»: потребность суммируем по полуфабрикатам,
      // потребление берём как есть (в строках оно одинаковое)
      item.months[row.month] = {
        consumptionKg: cell.consumptionKg,
        requirementKg:
          Math.round(
            (toNumber(cell.requirementKg) + toNumber(row.requirement_kg)) *
              1000
          ) / 1000
      };
    }
  });

  const items = Array.from(map.values()).sort((a, b) =>
    `${a.productName}|${a.semifinishedName || ""}`.localeCompare(
      `${b.productName}|${b.semifinishedName || ""}`,
      "ru"
    )
  );

  items.forEach((item: SemifinishedRequirementItem) => {
    (months || []).forEach((month: string) => {
      if (!item.months[month]) {
        item.months[month] = { consumptionKg: 0, requirementKg: 0 };
      }
    });
  });

  return items;
}
